// Date help for every date field of the app, mounted once at the root. It finds date inputs by their design
// placeholder (TT.MM.JJJJ / MM/JJJJ, also the French ones) or `data-date`, so the generated templates stay 1:1:
// - typing digits adds the dots/slash (mask.ts);
// - while such a field has focus, an own calendar (never the native one) opens below it. A click writes the date
//   like typing would (React's onChange runs) and leaves the field, which saves it as the fields do on blur.
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { isFr } from '../../i18n/lang';
import { applyEdit, dateKindOf, fmtDay, fmtMonth, maxLength, parseDate, type DateKind } from './mask';

const MONTHS_DE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const MONTHS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const SHORT_DE = ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sep.', 'Okt.', 'Nov.', 'Dez.'];
const SHORT_FR = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const DAYS_DE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'], DAYS_FR = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'];
const BLUE = '#1375BD', W = 252;

/** Writes a value the way typing does, so React's controlled inputs see it. */
function writeValue(el: HTMLInputElement, v: string) {
  Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')!.set!.call(el, v);
  el.dispatchEvent(new Event('input', { bubbles: true }));
}
/** Today in Luxembourg (the app's calendar day). */
function today() {
  const p = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Luxembourg', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()).split('-');
  return { y: +p[0]!, m: +p[1]!, d: +p[2]! };
}

interface Open { el: HTMLInputElement; kind: DateKind }

export function DateAssist() {
  const [open, setOpen] = useState<Open | null>(null);
  const [view, setView] = useState({ y: 2026, m: 1 }); // month shown (day kind) / year shown (month kind: m unused)
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const [, tick] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const closedFor = useRef<HTMLInputElement | null>(null); // Esc closes until the field is entered again

  const syncView = (el: HTMLInputElement, kind: DateKind) => {
    const p = parseDate(kind, el.value), t = today();
    setView(p ? { y: p.y, m: p.m } : { y: t.y, m: t.m });
  };
  const place = (el: HTMLInputElement) => {
    const r = el.getBoundingClientRect(), h = box.current?.offsetHeight || 300;
    const below = r.bottom + 6 + h <= window.innerHeight || r.top - 6 - h < 0;
    setPos({ left: Math.max(8, Math.min(r.left, window.innerWidth - W - 8)), top: below ? r.bottom + 6 : r.top - 6 - h });
  };

  useEffect(() => {
    const onFocus = (e: FocusEvent) => {
      const el = e.target as HTMLInputElement, kind = dateKindOf(el);
      if (!kind) return;
      if (closedFor.current !== el) closedFor.current = null;
      el.setAttribute('autocomplete', 'off'); el.maxLength = maxLength(kind);
      syncView(el, kind); setOpen({ el, kind }); requestAnimationFrame(() => place(el));
    };
    const onBlur = (e: FocusEvent) => {
      // Clicks inside the calendar keep the focus in the field (mousedown is prevented there).
      setOpen((o) => (o && o.el === e.target ? null : o));
      closedFor.current = null;
    };
    // Every edit goes through the strict mask (mask.ts): we write the result ourselves and fire `input`, so React's
    // onChange sees the masked value. Typing in the middle overwrites, the field never exceeds TT.MM.JJJJ.
    const onBefore = (e: Event) => {
      const el = e.target as HTMLInputElement, kind = dateKindOf(el), ie = e as InputEvent;
      if (!kind || ie.isComposing) return;
      const r = applyEdit(kind, el.value, el.selectionStart ?? el.value.length, el.selectionEnd ?? el.value.length, { type: ie.inputType, data: ie.data ?? ie.dataTransfer?.getData('text/plain') });
      if (!r) return;
      e.preventDefault();
      if (r.value !== el.value) writeValue(el, r.value);
      el.setSelectionRange(r.caret, r.caret);
    };
    const onInput = (e: Event) => {
      const el = e.target as HTMLInputElement, kind = dateKindOf(el);
      if (!kind) return;
      // A 2-digit year may still be half typed („30.09.20“ → 2026): the calendar follows complete years only.
      const p = /\d{4}$/.test(el.value.trim()) ? parseDate(kind, el.value) : null;
      if (p) setView({ y: p.y, m: p.m });
      else if (kind === 'day') { const mm = /^\d{1,2}\.(\d{1,2})\./.exec(el.value); if (mm && +mm[1]! >= 1 && +mm[1]! <= 12) setView((v) => ({ y: v.y, m: +mm[1]! })); }
      tick((n) => n + 1);
    };
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLInputElement;
      if (!dateKindOf(el)) return;
      if (e.key === 'Escape') { setOpen((o) => { if (o) { e.stopPropagation(); closedFor.current = el; } return null; }); }
      else if (e.key === 'ArrowDown' && e.altKey) { const kind = dateKindOf(el)!; syncView(el, kind); setOpen({ el, kind }); }
      else if (e.key === 'Enter') setOpen(null);
    };
    const onMove = () => setOpen((o) => { if (o) { if (!o.el.isConnected) return null; place(o.el); } return o; });
    document.addEventListener('focusin', onFocus, true); document.addEventListener('focusout', onBlur, true);
    document.addEventListener('beforeinput', onBefore, true); document.addEventListener('input', onInput, true); document.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onMove, true); window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('focusin', onFocus, true); document.removeEventListener('focusout', onBlur, true);
      document.removeEventListener('beforeinput', onBefore, true); document.removeEventListener('input', onInput, true); document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onMove, true); window.removeEventListener('resize', onMove);
    };
  }, []);

  if (!open || closedFor.current === open.el) return null;
  const fr = isFr(), MONTHS = fr ? MONTHS_FR : MONTHS_DE, t = today(), cur = parseDate(open.kind, open.el.value);
  const pick = (v: string) => { const el = open.el; writeValue(el, v); setOpen(null); el.blur(); };
  const keep = (e: React.MouseEvent) => e.preventDefault();
  const nav = (d: number) => setView((v) => open.kind === 'month' ? { ...v, y: v.y + d } : (() => { const n = v.y * 12 + (v.m - 1) + d; return { y: Math.floor(n / 12), m: (n % 12) + 1 }; })());

  const btn = (label: string, onClick: () => void, title: string) => (
    <button type="button" title={title} onClick={onClick} className="rc-dp-nav"
      style={{ width: 28, height: 28, border: 'none', borderRadius: 6, background: 'transparent', color: '#CDD3D9', font: "500 16px/1 Inter,sans-serif", cursor: 'pointer' }}>{label}</button>
  );
  let grid: React.ReactNode;
  if (open.kind === 'day') {
    const first = new Date(Date.UTC(view.y, view.m - 1, 1)), lead = (first.getUTCDay() + 6) % 7, days = new Date(Date.UTC(view.y, view.m, 0)).getUTCDate();
    const cells: Array<{ y: number; m: number; d: number; out: boolean }> = [];
    for (let i = 0; i < 42; i++) {
      const dt = new Date(Date.UTC(view.y, view.m - 1, 1 - lead + i));
      cells.push({ y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate(), out: dt.getUTCMonth() + 1 !== view.m });
    }
    const rows = lead + days > 35 ? 42 : 35;
    grid = (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
        {(fr ? DAYS_FR : DAYS_DE).map((d) => <div key={d} style={{ textAlign: 'center', font: "600 10px/22px Inter,sans-serif", color: '#7E797A', letterSpacing: '.04em' }}>{d.toUpperCase()}</div>)}
        {cells.slice(0, rows).map((c) => {
          const sel = cur && cur.y === c.y && cur.m === c.m && cur.d === c.d, isT = t.y === c.y && t.m === c.m && t.d === c.d;
          return (
            <button key={c.y + '-' + c.m + '-' + c.d} type="button" className="rc-dp-day" onClick={() => pick(fmtDay(c.y, c.m, c.d))}
              style={{ height: 30, border: isT && !sel ? '1px solid rgba(19,117,189,.8)' : '1px solid transparent', borderRadius: 6, cursor: 'pointer',
                background: sel ? BLUE : 'transparent', color: sel ? '#fff' : c.out ? '#5E595A' : '#FBFAFA', font: `${sel || isT ? 600 : 400} 12px/1 'JetBrains Mono',monospace` }}>{c.d}</button>
          );
        })}
      </div>
    );
  } else {
    grid = (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 4 }}>
        {MONTHS.map((name, i) => {
          const sel = cur && cur.y === view.y && cur.m === i + 1, isT = t.y === view.y && t.m === i + 1;
          return (
            <button key={name} type="button" className="rc-dp-day" onClick={() => pick(fmtMonth(view.y, i + 1))}
              style={{ height: 36, border: isT && !sel ? '1px solid rgba(19,117,189,.8)' : '1px solid transparent', borderRadius: 6, cursor: 'pointer',
                background: sel ? BLUE : 'transparent', color: sel ? '#fff' : '#FBFAFA', font: `${sel ? 600 : 400} 12px/1 Inter,sans-serif` }}>{(fr ? SHORT_FR : SHORT_DE)[i]}</button>
          );
        })}
      </div>
    );
  }
  const title = open.kind === 'day' ? MONTHS[view.m - 1] + ' ' + view.y : String(view.y);
  return createPortal(
    <div ref={box} onMouseDown={keep} role="dialog" aria-label={fr ? 'Choisir une date' : 'Datum wählen'}
      style={{ position: 'fixed', left: pos.left, top: pos.top, width: W, zIndex: 10001, padding: 10, borderRadius: 8, background: '#2B2627',
        border: '1px solid rgba(255,255,255,.12)', boxShadow: '0 12px 32px rgba(0,0,0,.45)', userSelect: 'none' }}>
      <style>{'.rc-dp-nav:hover,.rc-dp-day:hover{background:rgba(255,255,255,.08)!important}.rc-dp-foot:hover{color:#FBFAFA!important}'}</style>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        {btn('‹', () => nav(-1), open.kind === 'day' ? (fr ? 'Mois précédent' : 'Voriger Monat') : (fr ? 'Année précédente' : 'Voriges Jahr'))}
        <div style={{ font: "600 13px/1 Inter,sans-serif", color: '#FBFAFA', textTransform: 'capitalize' }}>{title}</div>
        {btn('›', () => nav(1), open.kind === 'day' ? (fr ? 'Mois suivant' : 'Nächster Monat') : (fr ? 'Année suivante' : 'Nächstes Jahr'))}
      </div>
      {grid}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,.08)' }}>
        <button type="button" className="rc-dp-foot" onClick={() => pick(open.kind === 'day' ? fmtDay(t.y, t.m, t.d) : fmtMonth(t.y, t.m))}
          style={{ border: 'none', background: 'transparent', color: '#6DA8DC', font: "600 12px/1 Inter,sans-serif", cursor: 'pointer', padding: '4px 2px' }}>
          {open.kind === 'day' ? (fr ? 'Aujourd’hui' : 'Heute') : (fr ? 'Ce mois' : 'Dieser Monat')}
        </button>
        <span style={{ font: "400 11px/1 Inter,sans-serif", color: '#7E797A' }}>{open.kind === 'day' ? (fr ? 'ou taper 30092026' : 'oder 30092026 tippen') : (fr ? 'ou taper 092026' : 'oder 092026 tippen')}</span>
      </div>
    </div>,
    document.body,
  );
}
